import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Download, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useCurrentDomain } from '../../context/DomainContext';
import { getBillingReport } from '../../../src/services/masterDataService';
import moment from 'moment';
import './BillingReport.css';
import { predefinedBottomRanges, selectStyles } from '../../helpers';

const BRAND_GREEN = '#0aa375';
const COLUMN_DIVIDER = '1px solid #e5e7eb';
const MOBILE_BREAKPOINT = 768;

const COLUMNS = [
    { label: 'Account',   key: 'account', align: 'left' },
    { label: 'Period',    key: 'period', align: 'left' },
    { label: 'Item',      key: 'item', align: 'left' },
    { label: 'Quantity',  key: 'quantity', align: 'right' },
    { label: 'Rate',      key: 'rate', align: 'right' },
    { label: 'Amount',    key: 'amount', align: 'right' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const pageSizeSelectOptions = PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }));

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const num = (n) => new Intl.NumberFormat('en-US').format(n || 0);

const BillingReport = () => {
    const { domain } = useCurrentDomain();

    const [records, setRecords]         = useState([]);
    const [allRecords, setAllRecords]   = useState([]);
    const [aggLoading, setAggLoading]   = useState(false);
    const [totalCount, setTotalCount]   = useState(0);
    const [loading, setLoading]         = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [pageNumber, setPageNumber]   = useState(1);
    const [pageSize, setPageSize]       = useState(50);
    const [activeTab, setActiveTab]     = useState('summary');

    const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 35); return d; });
    const [toDate, setToDate]     = useState(new Date());
    const [isExcelHovered, setIsExcelHovered] = useState(false);
    const [colWidths, setColWidths] = useState([160, 150, 200, 100, 90, 120]);

    const activeIndex = useRef(null);
    const startX      = useRef(0);
    const startWidth  = useRef(0);

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleMouseDown = (index, e) => {
        activeIndex.current = index;
        startX.current      = e.clientX;
        startWidth.current  = colWidths[index];
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        if (activeIndex.current === null) return;
        const computedWidth = Math.max(60, startWidth.current + (e.clientX - startX.current));
        setColWidths((prev) => {
            const updated = [...prev];
            updated[activeIndex.current] = computedWidth;
            return updated;
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        activeIndex.current = null;
        document.body.style.userSelect = '';
    }, [handleMouseMove]);

    useEffect(() => () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove, handleMouseUp]);

    const fetchRecords = useCallback(async (page, size, isNewSearch) => {
        if (!fromDate || !toDate) {
            toast.error('Please select a date range');
            return;
        }
        const setLoader = isNewSearch ? setSearchLoading : setLoading;
        setLoader(true);
        try {
            const payload = {
                domain,
                fromDate: moment(fromDate).format('YYYY-MM-DD'),
                toDate: moment(toDate).format('YYYY-MM-DD'),
                pageNumber: page,
                pageSize: size,
                isExport: false,
            };
            const res = await getBillingReport(payload);
            setRecords(res.details || []);
            setTotalCount(res.totalCount || 0);
            setPageNumber(res.pageNumber || page);
            setPageSize(res.pageSize || size);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load billing report');
            console.error(err);
        } finally {
            setLoader(false);
        }
    }, [domain, fromDate, toDate]);

    const isFirstRun = useRef(true);

    const fetchAggregates = useCallback(async () => {
        if (!fromDate || !toDate) return;
        setAggLoading(true);
        try {
            const payload = {
                domain,
                fromDate: moment(fromDate).format('YYYY-MM-DD'),
                toDate: moment(toDate).format('YYYY-MM-DD'),
                isExport: true,
            };
            const res = await getBillingReport(payload);
            setAllRecords(res?.details || []);
        } catch (err) {
            console.error(err);
        } finally {
            setAggLoading(false);
        }
    }, [domain, fromDate, toDate]);

    useEffect(() => {
        if (!domain) return;
        fetchRecords(1, pageSize, true);
        fetchAggregates();
    }, [domain]);

    useEffect(() => {
        if (isFirstRun.current) { isFirstRun.current = false; return; }
        if (!fromDate || !toDate) return;
        fetchRecords(1, pageSize, true);
        fetchAggregates();
    }, [fromDate, toDate]);

    const handleSearch = () => {
        fetchRecords(1, pageSize, true);
        fetchAggregates();
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const rangeStart  = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
    const rangeEnd    = Math.min(pageNumber * pageSize, totalCount);

    const goToPage = (page) => {
        if (page < 1 || page > totalPages || loading) return;
        fetchRecords(page, pageSize, false);
    };
    const handlePageSizeChange = (opt) => {
        fetchRecords(1, opt.value, false);
    };

    const totalAmount = allRecords.reduce((s, x) => s + (x.amount || 0), 0);
    const totalQty    = allRecords.reduce((s, x) => s + (x.quantity || 0), 0);
    const accountCount = new Set(allRecords.map((x) => x.account)).size;
    const avgPerAccount = accountCount ? totalAmount / accountCount : 0;

    const accountBreakdown = React.useMemo(() => {
        const byAccount = new Map();
        allRecords.forEach((r) => {
            byAccount.set(r.account, (byAccount.get(r.account) || 0) + (r.amount || 0));
        });
        return Array.from(byAccount.entries())
            .map(([account, amount]) => ({ label: account, amount }))
            .sort((a, b) => b.amount - a.amount);
    }, [allRecords]);

    const monthBreakdown = React.useMemo(() => {
        const byMonth = new Map();
        allRecords.forEach((r) => {
            const key = `${r.billGeneratedYear}-${String(r.billGeneratedMM).padStart(2, '0')}`;
            const label = `${r.billGeneratedMonth}-${r.billGeneratedYear}`;
            if (!byMonth.has(key)) byMonth.set(key, { key, label, amount: 0 });
            byMonth.get(key).amount += r.amount || 0;
        });
        return Array.from(byMonth.values()).sort((a, b) => a.key.localeCompare(b.key));
    }, [allRecords]);

    const maxAccountAmount = Math.max(1, ...accountBreakdown.map((x) => x.amount));
    const maxMonthAmount = Math.max(1, ...monthBreakdown.map((x) => x.amount));

    const exportToExcel = async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select a date range');
            return;
        }
        setExportLoading(true);
        const tid = toast.loading('Preparing export...');
        try {
            // Summary Sheet
            const summaryData = [
                { Metric: 'Total Amount', Value: totalAmount },
                { Metric: 'Total Quantity', Value: totalQty },
                { Metric: 'Accounts', Value: accountCount },
                { Metric: 'Avg / Account', Value: avgPerAccount },
                {},
                { Metric: '--- Billing by Account ---', Value: '' }
            ];
            accountBreakdown.forEach(row => {
                summaryData.push({ Metric: row.label, Value: row.amount });
            });
            summaryData.push({});
            summaryData.push({ Metric: '--- Billing by Month ---', Value: '' });
            monthBreakdown.forEach(row => {
                summaryData.push({ Metric: row.label, Value: row.amount });
            });

            // Detailed Sheet
            const exportData = allRecords.map((row) => ({
                ACCOUNT: row.account || '',
                MONTH: row.billGeneratedMonth || '',
                YEAR: row.billGeneratedYear || '',
                ITEM: row.item || '',
                QUANTITY: row.quantity || 0,
                RATE: row.rate || 0,
                AMOUNT: row.amount || 0,
            }));

            if (!exportData.length) {
                toast.error('No data to export');
                return;
            }

            const wsSummary = XLSX.utils.json_to_sheet(summaryData);
            const wsDetailed = XLSX.utils.json_to_sheet(exportData);
            
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
            XLSX.utils.book_append_sheet(wb, wsDetailed, 'Detailed Records');
            
            XLSX.writeFile(wb, `AdminBillingReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${exportData.length} record${exportData.length !== 1 ? 's' : ''}`);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to export');
        } finally {
            setExportLoading(false);
            toast.dismiss(tid);
        }
    };

    const getItemBadgeStyles = (item = '') => {
        const u = item.toUpperCase();
        if (u.includes('NYSIF')) return { backgroundColor: '#e6f7f2', color: BRAND_GREEN };
        if (u.includes('MANUAL')) return { backgroundColor: '#f1f5f9', color: '#475569' };
        if (u.includes('PAP')) return { backgroundColor: '#fef3e7', color: '#b45309' };
        return { backgroundColor: '#eef2ff', color: '#3730a3' };
    };

    const tdStyle = (i, isLast = false) => ({
        width: `${colWidths[i]}px`,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        borderRight: isLast ? 'none' : COLUMN_DIVIDER,
        verticalAlign: 'top',
        textAlign: COLUMNS[i]?.align || 'left',
    });

    const filterLabelStyle = { fontSize: '10px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px', display: 'block' };

    return (
        <div className="bg-light min-vh-100 abr-page-wrap">
            <Toaster position="top-right" />
            <div className="card shadow-sm border-0 rounded-3 bg-white abr-card">
                <div className="border-bottom px-2 py-2" style={{ backgroundColor: '#fcfcfc' }}>
                    <div className="d-flex gap-2 align-items-end justify-content-between abr-toolbar-row">
                        <div className="abr-date-filter">
                            <label style={filterLabelStyle}>Bill Date Range</label>
                            <DateRangePicker
                                value={[fromDate, toDate]}
                                onChange={(value) => {
                                    if (!value || !Array.isArray(value) || value.length !== 2) {
                                        setFromDate(null);
                                        setToDate(null);
                                        return;
                                    }
                                    const [start, end] = value;
                                    setFromDate(start || null);
                                    setToDate(end || null);
                                }}
                                ranges={predefinedBottomRanges}
                                placeholder="Select Date Range"
                                style={{ width: '100%' }}
                                format="MM/dd/yyyy"
                                size="sm"
                                cleanable
                                placement="bottomStart"
                                preventOverflow
                                showOneCalendar={isMobile}
                                popperClassName="abr-datepicker-popper"
                            />
                        </div>

                        <div className="d-flex gap-2 abr-toolbar-actions">
                            <button
                                className="btn text-white fw-medium d-flex align-items-center justify-content-center gap-1 px-3 abr-toolbar-btn"
                                style={{ backgroundColor: BRAND_GREEN, height: '32px', fontSize: '12px' }}
                                onClick={handleSearch} disabled={loading || searchLoading || exportLoading} title="Search"
                            >
                                {searchLoading
                                    ? <RefreshCw size={13} style={{ animation: 'abr-spin 1s linear infinite', color: '#fff' }} />
                                    : <Search size={13} style={{ color: '#fff' }} />}
                                <span className="abr-btn-label">Search</span>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 px-3 abr-toolbar-btn"
                                style={{ height: '32px', fontSize: '12px', backgroundColor: isExcelHovered ? '#f8f9fa' : '#fff', color: '#333' }}
                                onMouseEnter={() => setIsExcelHovered(true)}
                                onMouseLeave={() => setIsExcelHovered(false)}
                                onClick={exportToExcel} disabled={exportLoading || loading || searchLoading || aggLoading} title="Export to Excel"
                            >
                                {exportLoading
                                    ? <RefreshCw size={13} style={{ animation: 'abr-spin 1s linear infinite', color: '#6c757d' }} />
                                    : <Download size={13} style={{ color: '#6c757d' }} />}
                                <span className="abr-btn-label">{exportLoading ? 'Exporting...' : 'Export'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="px-3 pt-2 border-bottom mb-3 d-flex gap-3 justify-content-start">
                    <button
                        className="btn btn-sm fw-medium pb-2 px-1"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: activeTab === 'summary' ? BRAND_GREEN : '#6c757d',
                            borderBottom: activeTab === 'summary' ? `2px solid ${BRAND_GREEN}` : '2px solid transparent',
                            borderRadius: 0,
                            boxShadow: 'none',
                        }}
                        onClick={() => setActiveTab('summary')}
                    >
                        Summary View
                    </button>
                    <button
                        className="btn btn-sm fw-medium pb-2 px-1"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: activeTab === 'detailed' ? BRAND_GREEN : '#6c757d',
                            borderBottom: activeTab === 'detailed' ? `2px solid ${BRAND_GREEN}` : '2px solid transparent',
                            borderRadius: 0,
                            boxShadow: 'none',
                        }}
                        onClick={() => setActiveTab('detailed')}
                    >
                        Detailed View
                    </button>
                </div>

                {activeTab === 'summary' && (
                    <>
                        <div className="abr-kpi-row">
                    <div className="abr-kpi">
                        <div className="abr-kpi-label">Total Amount</div>
                        <div className="abr-kpi-value">{money(totalAmount)}</div>
                    </div>
                    <div className="abr-kpi">
                        <div className="abr-kpi-label">Total Quantity</div>
                        <div className="abr-kpi-value">{num(totalQty)}</div>
                    </div>
                    <div className="abr-kpi">
                        <div className="abr-kpi-label">Accounts</div>
                        <div className="abr-kpi-value">{accountCount}</div>
                    </div>
                    <div className="abr-kpi">
                        <div className="abr-kpi-label">Avg / Account</div>
                        <div className="abr-kpi-value">{money(avgPerAccount)}</div>
                    </div>
                </div>

                <div className="abr-breakdown-row">
                    <div className="abr-breakdown-card">
                        <div className="abr-breakdown-title">Billing by Account</div>
                        <div className="abr-bar-list">
                            {(loading || searchLoading || aggLoading) ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-success spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : accountBreakdown.length === 0 ? (
                                <div className="abr-bar-empty">No data available.</div>
                            ) : (
                                accountBreakdown.map((row) => (
                                    <div className="abr-bar-row" key={row.label}>
                                        <div className="abr-bar-row-label" title={row.label}>{row.label}</div>
                                        <div className="abr-bar-track">
                                            <div className="abr-bar-fill" style={{ width: `${(row.amount / maxAccountAmount) * 100}%` }} />
                                        </div>
                                        <div className="abr-bar-row-value">{money(row.amount)}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="abr-breakdown-card">
                        <div className="abr-breakdown-title">Billing by Month</div>
                        <div className="abr-bar-list">
                            {(loading || searchLoading || aggLoading) ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-success spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : monthBreakdown.length === 0 ? (
                                <div className="abr-bar-empty">No data available.</div>
                            ) : (
                                monthBreakdown.map((row) => (
                                    <div className="abr-bar-row" key={row.key}>
                                        <div className="abr-bar-row-label" title={row.label}>{row.label}</div>
                                        <div className="abr-bar-track">
                                            <div className="abr-bar-fill" style={{ width: `${(row.amount / maxMonthAmount) * 100}%` }} />
                                        </div>
                                        <div className="abr-bar-row-value">{money(row.amount)}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                    </>
                )}

                {activeTab === 'detailed' && (
                    <>
                        <div className="table-responsive abr-desktop-table" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <table
                        className="table table-hover align-middle mb-0 abr-detailed-table"
                        style={{ tableLayout: 'fixed', fontSize: '12.5px', width: '100%', minWidth: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
                    >
                        <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                            <tr>
                                {COLUMNS.map((col, index) => (
                                    <th
                                        key={index}
                                        className="text-secondary fw-bold text-uppercase py-1 px-2 position-relative"
                                        style={{
                                            width: `${colWidths[index]}px`,
                                            minWidth: `${colWidths[index]}px`,
                                            fontSize: '10.5px',
                                            letterSpacing: '0.4px',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            borderRight: index < COLUMNS.length - 1 ? COLUMN_DIVIDER : 'none',
                                            textAlign: col.align || 'left',
                                        }}
                                    >
                                        {col.label}
                                        <div
                                            onMouseDown={(e) => handleMouseDown(index, e)}
                                            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 11 }}
                                            className="abr-column-resizer-handle"
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(loading || searchLoading) ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-5 border-0">
                                        <div className="spinner-border text-success" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : records.length === 0 ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-4 abr-empty-state border-0">
                                        No matching records found.
                                    </td>
                                </tr>
                            ) : (
                                records.map((row, idx) => {
                                    const badgeStyles = getItemBadgeStyles(row.item);
                                    return (
                                        <tr key={`${row.account}-${row.item}-${idx}`} className={idx % 2 === 0 ? 'abr-row-even' : 'abr-row-odd'}>
                                            <td className="py-1 px-2 text-dark fw-medium" title={row.account} style={{ ...tdStyle(0), textTransform: 'uppercase', fontSize: '11.5px' }}>
                                                {row.account || '—'}
                                            </td>
                                            <td className="text-secondary py-1 px-2" style={tdStyle(1)}>
                                                {row.billGeneratedMonth} {row.billGeneratedYear}
                                            </td>
                                            <td className="py-1 px-2" style={tdStyle(2)}>
                                                <span className="badge fw-bold abr-px-15 abr-py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                    {row.item}
                                                </span>
                                            </td>
                                            <td className="py-1 px-2" style={tdStyle(3)}>{num(row.quantity)}</td>
                                            <td className="py-1 px-2" style={tdStyle(4)}>{money(row.rate)}</td>
                                            <td className="py-1 px-2 abr-amount-col" style={tdStyle(5, true)}>{money(row.amount)}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="abr-mobile-cards">
                    {(loading || searchLoading) ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-success" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : records.length === 0 ? (
                        <div className="text-center py-4 text-muted">No matching records found.</div>
                    ) : (
                        records.map((row, idx) => {
                            const badgeStyles = getItemBadgeStyles(row.item);
                            return (
                                <div key={`card-${row.account}-${row.item}-${idx}`} className="abr-ecard">
                                    <div className="abr-ecard-meta-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div className="abr-ecard-meta-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="abr-ecard-meta-label">Account</span>
                                            <span className="abr-ecard-meta-value" title={row.account} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.account || '—'}</span>
                                        </div>
                                        <div className="abr-ecard-meta-item">
                                            <span className="abr-ecard-meta-label">Period</span>
                                            <span className="abr-ecard-meta-value">{row.billGeneratedMonth} {row.billGeneratedYear}</span>
                                        </div>
                                        <div className="abr-ecard-meta-item">
                                            <span className="abr-ecard-meta-label">Item</span>
                                            <span className="abr-ecard-meta-value">
                                                <span className="badge fw-bold abr-px-15 abr-py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                    {row.item}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="abr-ecard-divider" />

                                    <div className="abr-ecard-meta-grid">
                                        <div className="abr-ecard-meta-item">
                                            <span className="abr-ecard-meta-label">Qty</span>
                                            <span className="abr-ecard-meta-value">{num(row.quantity)}</span>
                                        </div>
                                        <div className="abr-ecard-meta-item">
                                            <span className="abr-ecard-meta-label">Rate</span>
                                            <span className="abr-ecard-meta-value">{money(row.rate)}</span>
                                        </div>
                                        <div className="abr-ecard-meta-item">
                                            <span className="abr-ecard-meta-label">Amount</span>
                                            <span className="abr-ecard-meta-value abr-amount">{money(row.amount)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {!loading && !searchLoading && records.length > 0 && (
                    <div
                        className="abr-pagination-bar"
                        style={{
                            padding: '10px 20px', borderTop: '1px solid #e5e7eb',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#fafafa', flexWrap: 'wrap', gap: '8px',
                        }}
                    >
                        <span className="abr-pagination-summary" style={{ fontSize: 12, color: '#6b7280' }}>
                            {totalCount === 0 ? 'No records' : <>Showing {rangeStart}–{rangeEnd} of {totalCount}</>}
                        </span>

                        <div className="abr-pagination-controls" style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                            <button
                                onClick={() => goToPage(pageNumber - 1)}
                                disabled={pageNumber === 1}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                                    background: pageNumber === 1 ? '#f3f4f6' : '#fff',
                                    color: pageNumber === 1 ? '#9ca3af' : BRAND_GREEN,
                                    cursor: pageNumber === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                                }}
                            >‹ Prev</button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => totalPages <= 7 || p === 1 || p === totalPages || (p >= pageNumber - 2 && p <= pageNumber + 2))
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => goToPage(p)}
                                            style={{
                                                width: 28, height: 28, borderRadius: 6, border: '1px solid #d1fae5',
                                                background: pageNumber === p ? BRAND_GREEN : '#fff',
                                                color: pageNumber === p ? '#fff' : BRAND_GREEN,
                                                fontWeight: pageNumber === p ? 700 : 400,
                                                cursor: 'pointer', fontSize: 12
                                            }}
                                        >{p}</button>
                                    )
                                )}

                            <button
                                onClick={() => goToPage(pageNumber + 1)}
                                disabled={pageNumber === totalPages}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                                    background: pageNumber === totalPages ? '#f3f4f6' : '#fff',
                                    color: pageNumber === totalPages ? '#9ca3af' : BRAND_GREEN,
                                    cursor: pageNumber === totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                                }}
                            >Next ›</button>
                        </div>

                        <div className="abr-pagesize-select" style={{ width: '110px' }}>
                            <Select
                                options={pageSizeSelectOptions}
                                value={pageSizeSelectOptions.find((o) => o.value === pageSize)}
                                onChange={handlePageSizeChange}
                                isSearchable={false}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                classNamePrefix="react-select"
                            />
                        </div>
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BillingReport;